import React, { useMemo } from 'react';
import { chartAllColors } from '@op/constants';
import EChartCommon, { getAxisPointer } from '@op/components/EChartCommon';
import { formatTimeXAxisText } from '@op/utils/chart';
import { getNeedTime } from '@op/utils/time';
import {
  handleMentionData,
  sortMapAuxiliaryFunc,
  supplementSingleData,
} from '@op/utils';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@op/components/ChartCommon/formatter';
import { reviewsFormat, yAxleFormat } from '@op/utils/value';
import { useIntl } from 'umi';

export default ({
  height = 330,
  data,
  type,
  dateType,
  title,
  translate = (value: string) => value,
  noTotal = false,
  sortMap,
  colorMap = {},
}: any) => {
  const { formatMessage } = useIntl();
  const dealwithData = () => {
    const { timeline_of_start_time, timeline_of_end_time, timeline } = data;
    if (!timeline || timeline.length === 0) {
      return { newArr: [], timeArr: [] };
    }
    const needTime = getNeedTime(dateType);
    const timeLineInfo = supplementSingleData(
      timeline_of_start_time,
      timeline_of_end_time,
      timeline.map((item: any) => ({ ...item, count: item.value })),
      dateType,
    );

    return handleMentionData(timeLineInfo, needTime);
  };

  const [options, empty] = useMemo(() => {
    const seriesData: any = [];
    const legendData: Array<any> = [];
    const { newArr, timeArr } = dealwithData();
    newArr.forEach(({ data, name }: any, index: number) => {
      const color = colorMap[name] || chartAllColors[index];
      const seriesItem: any = {
        data,
        type: type,
        name: name,
        itemStyle: { color },
      };
      if (type === 'bar') {
        seriesItem['stack'] = 'Total';
      }
      seriesData.push(seriesItem);
      legendData.push({
        name: name,
        icon: 'circle',
        itemStyle: { color },
      });
    });

    if (sortMap) {
      legendData.sort(sortMapAuxiliaryFunc(sortMap, (item) => item.name));
      seriesData.sort(sortMapAuxiliaryFunc(sortMap, (item) => item.name));
    }
    const opts = {
      tooltip: {
        formatter: (params: any) => {
          const time = listTimeTitleFunc(params[0].axisValue, dateType);
          return formatChartParamsDate({
            params,
            dealwithRecords(records, total) {
              records.sort(
                (a, b) => (b.originValue || 0) - (a.originValue || 0),
              );
              if (!noTotal) {
                records.unshift({
                  text: formatMessage({ id: 'Total' }),
                  value: reviewsFormat(total),
                  originValue: total,
                  originText: 'Total',
                });
              }
            },
            listTitleFunc: (item) => translate(item.seriesName),
            valueFormat: (value) => reviewsFormat(value),
            tablesConfigFunc: (records: Array<any>) => [
              {
                showHeader: true,
                records,
                columns: [
                  {
                    key: 'text',
                    weight: 'bold',
                    header: time,
                  },
                  {
                    key: 'value',
                    align: 'right',
                    weight: 'bold',
                    header: title,
                  },
                ],
              },
            ],
          });
        },
      },
      grid: { top: 20 },
      legend: {
        data: legendData,
        formatter: translate,
      },
      axisPointer: getAxisPointer((data) => {
        return formatTimeXAxisText(data.value, dateType);
      }),
      xAxis: {
        data: timeArr,
        axisLabel: {
          formatter: (value: string) => formatTimeXAxisText(value, dateType),
        },
      },
      yAxis: {
        axisLabel: {
          formatter: (value: number) => yAxleFormat(value),
        },
      },
      series: seriesData,
    };

    return [opts, newArr.length === 0];
  }, [data, type, title, noTotal, translate]);

  return (
    <EChartCommon
      optionsProps={{ type: type as 'line' | 'bar' }}
      options={options}
      height={height}
      empty={empty}
    ></EChartCommon>
  );
};
